import { gql } from '@apollo/client'

/**
 * GraphQL documents for the notifications bell (EL-2463).
 *
 * Replaces `GET /v3/notifications/user`, which stays in service for Android and iOS — see
 * `api-notifications.js`. One `queries.js` per service, the same convention
 * `lib/services/comments/queries.js` follows.
 *
 * ⚠️ These are consumed from the SAGA, not from a hook. The bell has polling, a cross-tab unread
 * badge, four independent `takeLatest` surfaces and recent-unchanged collapsing; none of that
 * survives a rewrite to `useQuery`. `lib/services/tiles/watchers.js` is the precedent for calling
 * the Apollo client directly from a watcher.
 *
 * @module services/notifications/queries
 */

/**
 * The content a notification points at, hydrated SERVER-side.
 *
 * ⚠️ This selection is the whole point of the migration. Web used to fetch these rows itself,
 * after every page, through `fetchMultipleNodes` plus a `Promise.all` of member lookups. Asking
 * for them here costs one batched query per field on the server and no client round trip — so
 * nothing in this service may reintroduce a follow-up fetch to fill a field in.
 *
 * The field names are the GATEWAY's, which are not the REST node's: `contentId` not `nid`,
 * `contentSeries` not `series`, and an image comes off `contentAssetReferences` rather than a
 * `keyart_*` bag. `feedAdapter.js` is where that translation lives, so the renderers keep reading
 * the shape they already read.
 */
export const NOTIFICATION_CONTENT_FIELDS = gql`
  fragment NotificationContentFields on Content {
    contentId
    contentType
    title
    path
    language
    featureMediaId
    contentSeries {
      # contentId maps onto the node's nid, which GA4 reads as parent_series_id.
      contentId
      title
      path
    }
    # ⚠️ NOT filtered server-side, deliberately. Types come back on each reference;
    # feedAdapter keeps notification tiles to 16x9 keyart only.
    contentAssetReferences {
      contentAssetReferenceType {
        type
      }
      asset {
        renderUrl
        language
      }
    }
    # Admin Images slot — same d6 keyart types when Assets refs are missing (EL-2463).
    imageContentRelationships(types: ["d6-keyart-with-text", "d6-keyart-no-text"]) {
      type
      language
      image {
        url
      }
    }
  }
`

/** The actor and member-target profile share a selection — they are the same type. */
export const NOTIFICATION_PROFILE_FIELDS = gql`
  fragment NotificationProfileFields on UserProfile {
    uuid
    displayName
    avatar {
      url
      small
    }
  }
`

/**
 * One page of the member's bell.
 *
 * ⚠️ `category` is OMITTED when counting the unread badge. `unseenCount` is deliberately not
 * narrowed by it — the badge answers "is anything new", not "is anything new in the tab I happen
 * to be looking at" (CO-768). `totalCount` DOES respect it, because that one is the tab's count.
 *
 * ⚠️ Cursor paging, not page numbers. Notifications arrive at the top of this list constantly,
 * which is exactly when an offset repeats or skips a row between two pages. Pass
 * `pageInfo.endCursor` as `after`; stop on `hasNextPage: false`. Never synthesise a page index
 * from a cursor.
 */
export const GET_NOTIFICATION_FEED = gql`
  ${NOTIFICATION_CONTENT_FIELDS}
  ${NOTIFICATION_PROFILE_FIELDS}
  query NotificationFeed(
    $category: NotificationCategory
    $language: [String!]
    $first: Int
    $after: String
  ) {
    notificationFeed(
      category: $category
      language: $language
      first: $first
      after: $after
    ) {
      totalCount
      unseenCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          type
          category
          name
          label
          reason
          message
          received
          startDate
          # The bell hides an expired video promotion on this; without it isExpired is
          # permanently false and the promotion renders for ever.
          endDate
          # Aggregated reactions report how many members they stand for, so the client can
          # pluralise -- see circleCopy.circleReactionCopy.
          actorCount
          actor {
            ...NotificationProfileFields
          }
          links {
            id
            type
            path
            external
          }
          target {
            __typename
            ... on NotificationContentTarget {
              contentId
              contentType
              content {
                ...NotificationContentFields
              }
            }
            ... on NotificationCommentTarget {
              commentId
              contentId
              content {
                ...NotificationContentFields
              }
            }
            # WARNING: name here is the CIRCLE's name, not the actor's. The row's own name
            # holds the ACTOR on the reply and mention types. Web composes its own localised
            # sentence around it (CO-751): the stored message on these rows is English-only.
            ... on NotificationCircleTarget {
              circleUuid
              postUuid
              name
            }
            ... on NotificationMemberTarget {
              uid
              profile {
                ...NotificationProfileFields
              }
            }
          }
        }
      }
    }
  }
`

/**
 * Mark notifications received, clearing them from the unread badge.
 *
 * ⚠️ Idempotent. An id already marked received is accepted and counted zero, so a retry answers
 * `markedCount: 0` — that is success, not failure. Ids belonging to another member come back in
 * `ignoredIds` rather than erroring.
 */
export const MARK_NOTIFICATIONS_RECEIVED = gql`
  mutation NotificationsMarkReceived($ids: [ID!]!) {
    notificationsMarkReceived(ids: $ids) {
      success
      markedCount
      ignoredIds
    }
  }
`
